import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useErrorMessages } from '../../../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../../../models/Logging';
import { useDeleteStripeSessionMutation } from '../../../../../../../../queries';
import type {
	IEventRegistrationSurvey,
	IEventSurveyResponse,
	ILineItem,
	IPublicFileAttachment,
	IRecipient,
} from '../../../../../../../models/Api';

interface ITicketHolder {
	id?: string; // Unique ID from backend or generated UUID
	ticketId: string;
	ticketName: string;
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	isPaid?: boolean; // Whether this ticket has been paid for
}

interface IFormValues {
	isAttending: boolean | null;
	isPaid: boolean;
	bringingGuest?: boolean;
	email?: string;
	firstName?: string;
	lastName?: string;
	phone?: string;
	guests: IRecipient[];
	customProperties: Record<string, string | IPublicFileAttachment[]>;
	ticketSelections?: Record<string, number>;
	ticketHolders?: ITicketHolder[];
}

interface UsePaymentProcessingProps {
	survey?: IEventRegistrationSurvey;
	surveyResponse?: IEventSurveyResponse;
	formValues: IFormValues;
	processingPayment: React.MutableRefObject<boolean>;
	setShowCheckoutForm: (show: boolean) => void;
	ticketsOrder: number;
	setTicketsOrder: (order: number) => void;
	setPaidTickets: (tickets: number) => void;
	urlParams: { token?: string };
}

export const usePaymentProcessing = ({
	survey,
	surveyResponse,
	formValues,
	processingPayment,
	setShowCheckoutForm,
	ticketsOrder,
	setTicketsOrder,
	setPaidTickets,
	urlParams,
}: UsePaymentProcessingProps) => {
	const history = useHistory();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('EventSurveyForm');

	const deleteStripeSession = useDeleteStripeSessionMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('DeleteStripeSession-Error', error);
		},
		onSuccess: async () => {
			setShowCheckoutForm(false);
		},
	});

	const resetUrlToToken = React.useCallback(() => {
		history.replace({
			pathname: `/event/${urlParams.token}`,
			search: '',
		});
	}, [history, urlParams.token]);

	const getPaidTickets = React.useCallback(() => {
		if (surveyResponse?.payments?.some(p => p.status === 'PaymentSucceeded') || processingPayment.current) {
			// Count paid attendees and guests using the isPaid flag
			const paidAttendee = surveyResponse?.attendee?.isPaid ? 1 : 0;
			const paidGuests = surveyResponse?.guests?.filter(guest => guest.isPaid).length || 0;
			return paidAttendee + paidGuests;
		}
		return 0;
	}, [surveyResponse, processingPayment]);

	React.useEffect(() => {
		if (survey?.isPaidEvent) {
			setPaidTickets(getPaidTickets());
		}
	}, [survey?.isPaidEvent, getPaidTickets, setPaidTickets]);

	// Calculate paid ticket quantities per ticket type
	const getPaidTicketQuantities = React.useCallback((): Record<string, number> => {
		const paidQuantities: Record<string, number> = {};

		if (surveyResponse?.payments?.some(p => p.status === 'PaymentSucceeded')) {
			// Count paid tickets from attendee and guests
			const allRecipients = [surveyResponse.attendee, ...(surveyResponse.guests || [])].filter(Boolean);

			allRecipients.forEach(recipient => {
				if (recipient?.isPaid && recipient?.ticket?.id) {
					const ticketId = recipient.ticket.id;
					paidQuantities[ticketId] = (paidQuantities[ticketId] || 0) + 1;
				}
			});
		}

		return paidQuantities;
	}, [surveyResponse]);

	const getTotalTicketsOrder = React.useCallback(() => {
		if (survey?.isPaidEvent && formValues.isAttending) {
			// For tiered tickets, calculate based on ticket selections minus paid tickets
			if (survey?.attendeeOptions?.eventTickets && survey.attendeeOptions.eventTickets.length > 0) {
				const selections = formValues.ticketSelections || {};
				const paidQuantities = getPaidTicketQuantities();

				// Calculate total unpaid tickets
				let totalUnpaidTickets = 0;
				Object.entries(selections).forEach(([ticketId, quantity]) => {
					const alreadyPaidQuantity = paidQuantities[ticketId] || 0;
					const unpaidQuantity = quantity - alreadyPaidQuantity;
					totalUnpaidTickets += Math.max(0, unpaidQuantity);
				});

				return totalUnpaidTickets;
			}

			// Legacy calculation for costPerAttendee
			const isBringingGuest = formValues.bringingGuest;
			const guestCount = formValues.guests?.length || 0;
			let totalTickets = 1; // Always start with 1 for the attendee

			if (!isBringingGuest) {
				// If not bringing guests, only count unpaid attendee
				const attendeeIsPaid = surveyResponse?.attendee?.isPaid || false;
				return attendeeIsPaid ? 0 : 1;
			}

			if (isBringingGuest && !guestCount) {
				// If bringing guest but no guest count, add 1 for anonymous guest
				totalTickets = totalTickets + 1;
			}

			if (guestCount) {
				// Add all guests to the total
				totalTickets = totalTickets + guestCount;
			}

			// Subtract already paid tickets from the total
			const paidAttendee = surveyResponse?.attendee?.isPaid ? 1 : 0;
			const paidGuests = surveyResponse?.guests?.filter(guest => guest.isPaid).length || 0;
			totalTickets = totalTickets - paidAttendee - paidGuests;

			return Math.max(0, totalTickets);
		}
		return 0;
	}, [
		formValues.bringingGuest,
		formValues.guests?.length,
		formValues.isAttending,
		formValues.ticketSelections,
		survey?.isPaidEvent,
		survey?.attendeeOptions?.eventTickets,
		surveyResponse?.attendee?.isPaid,
		surveyResponse?.guests,
		getPaidTicketQuantities,
	]);

	React.useEffect(() => {
		if (survey?.isPaidEvent) {
			setTicketsOrder(getTotalTicketsOrder());
		}
	}, [survey?.isPaidEvent, getTotalTicketsOrder, setTicketsOrder]);

	const formattedAmount = React.useMemo(() => {
		// For tiered tickets, calculate total from selections minus paid tickets
		if (survey?.attendeeOptions?.eventTickets && survey.attendeeOptions.eventTickets.length > 0) {
			const selections = formValues.ticketSelections || {};
			const tickets = survey.attendeeOptions.eventTickets;
			const paidQuantities = getPaidTicketQuantities();

			let totalCents = 0;
			Object.entries(selections).forEach(([ticketId, quantity]) => {
				const ticket = tickets.find(t => t.id === ticketId);
				const alreadyPaidQuantity = paidQuantities[ticketId] || 0;
				const unpaidQuantity = quantity - alreadyPaidQuantity;

				if (ticket && unpaidQuantity > 0) {
					totalCents += (ticket.unitPrice || 0) * unpaidQuantity;
				}
			});

			const dollarAmount = new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
			}).format(totalCents / 100);
			return dollarAmount;
		}

		// Legacy calculation for costPerAttendee
		if (survey?.attendeeOptions?.costPerAttendee) {
			const dollarAmount = new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
			}).format((survey?.attendeeOptions?.costPerAttendee * ticketsOrder) / 100);
			return dollarAmount;
		}
		return 0;
	}, [
		survey?.attendeeOptions?.costPerAttendee,
		survey?.attendeeOptions?.eventTickets,
		ticketsOrder,
		formValues.ticketSelections,
		getPaidTicketQuantities,
	]);

	// Build line items array from ticket selections for Stripe checkout
	const buildLineItems = React.useCallback((): ILineItem[] => {
		if (!formValues.ticketSelections || !survey?.attendeeOptions?.eventTickets) {
			return [];
		}

		const lineItems: ILineItem[] = [];
		const tickets = survey.attendeeOptions.eventTickets;
		const selections = formValues.ticketSelections;
		const paidQuantities = getPaidTicketQuantities();

		Object.entries(selections).forEach(([ticketId, quantity]) => {
			const ticket = tickets.find(t => t.id === ticketId);
			const alreadyPaidQuantity = paidQuantities[ticketId] || 0;
			// Only include NEW tickets that haven't been paid for yet
			const unpaidQuantity = quantity - alreadyPaidQuantity;

			if (ticket && unpaidQuantity > 0) {
				lineItems.push({
					itemId: ticketId,
					quantity: unpaidQuantity,
					name: ticket.name,
					description: ticket.description || ticket.name,
				});
			}
		});

		return lineItems;
	}, [formValues.ticketSelections, survey?.attendeeOptions?.eventTickets, getPaidTicketQuantities]);

	return {
		deleteStripeSession,
		resetUrlToToken,
		getPaidTickets,
		getPaidTicketQuantities,
		getTotalTicketsOrder,
		formattedAmount,
		buildLineItems,
	};
};
